import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Upload, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useState } from 'react';

interface SortableBonusCardProps {
  bonus: {
    id: string;
    title: string;
    thumbnailUrl?: string;
  };
  index: number;
  onUpdate: (id: string, updates: any) => void;
  onDelete: (id: string) => void;
  onImageUpload: (id: string, file: File) => void;
}

export function SortableBonusCard({ 
  bonus, 
  index, 
  onUpdate, 
  onDelete, 
  onImageUpload 
}: SortableBonusCardProps) {
  useLanguage();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: bonus.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    onDelete(bonus.id);
    setShowDeleteConfirm(false);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white border border-gray-200 rounded-xl p-3 space-y-2 relative group ${
        isDragging ? 'shadow-lg opacity-50 scale-[1.02] z-50' : ''
      }`}
    >
      {/* Drag Handle */}
      <button
        {...attributes}
        {...listeners}
        className="absolute top-1 left-1/2 -translate-x-1/2 cursor-grab active:cursor-grabbing 
                   text-gray-400 hover:text-gray-600 transition-colors touch-none z-20
                   bg-white/90 rounded-full p-1 shadow-sm"
      >
        <GripVertical className="w-4 h-4" />
      </button>

      {/* Badge de número */}
      <div className="absolute -top-2 -left-2 w-6 h-6 bg-[#3B82F6] rounded-full 
                      flex items-center justify-center text-white text-xs font-bold z-10">
        {index + 1}
      </div>
      
      {/* Thumbnail Upload */}
      <div className="relative">
        {/* Botão Excluir - Fora do label para evitar conflito */}
        <button
          type="button"
          onClick={handleDeleteClick}
          className="absolute top-2 right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full 
                     flex items-center justify-center text-white text-xs font-bold
                     opacity-0 group-hover:opacity-100 transition-all z-30 shadow-md"
        >
          ✕
        </button>
        
        <label className="aspect-[600/1050] bg-gray-100 rounded-lg overflow-hidden cursor-pointer block hover:bg-gray-200 transition-colors">
          {bonus.thumbnailUrl ? (
            <img 
              src={bonus.thumbnailUrl} 
              alt={bonus.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
              <Upload className="w-8 h-8 mb-1" />
              <span className="text-[10px]">{translateInterfaceText("Upload")}</span>
            </div>
          )}
          <input 
            type="file" 
            className="hidden" 
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onImageUpload(bonus.id, file);
            }}
          />
        </label>
      </div>
      
      {/* Input de Título */}
      <Input
        value={bonus.title}
        onChange={(e) => onUpdate(bonus.id, { title: e.target.value })}
        placeholder={translateInterfaceText("Título")}
        className="text-xs h-8"
      />

      {/* Modal de Confirmação */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent className="w-[92vw] sm:w-[clamp(360px,38vw,440px)] sm:max-w-none p-0 rounded-2xl">
          <AlertDialogHeader className="px-5 py-4">
            <AlertDialogTitle>{translateInterfaceText("Excluir Bônus?")}</AlertDialogTitle>
          </AlertDialogHeader>
          
          <div className="px-5 pb-4">
            <AlertDialogDescription>
              {translateInterfaceText("Tem certeza? Esta ação não pode ser desfeita.")}</AlertDialogDescription>
          </div>
          
          <AlertDialogFooter className="px-5 py-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => setShowDeleteConfirm(false)}
            >
              {translateInterfaceText("Cancelar")}</Button>
            <Button 
              variant="destructive" 
              onClick={handleConfirmDelete}
            >
              {translateInterfaceText("Excluir")}</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
